'use client'

import { useState, useEffect } from 'react'
import { useFeedSources } from './select-feed-sources'
import { UnifiedFeed } from './unified-feed'
import { Skeleton, SkeletonText } from '@/ui/skeleton'
import type { ViewMode } from '@/ui/view-switcher'
import type { FilterType } from './types'
import type { PromptCategory } from '@/entries'

interface UnifiedFeedLazyProps {
  /** Whether to load data - useful for mobile toggle */
  enabled?: boolean
  /** Debounced search query to filter entries */
  searchQuery?: string
  /** Raw search query for input display (non-debounced) */
  rawSearchQuery?: string
  /** Selected topic IDs to filter entries */
  selectedTopics?: string[]
  /** Handler for search query changes */
  onSearchChange?: (query: string) => void
  /** Handler for topic selection changes */
  onTopicsChange?: (topics: string[]) => void
  /** Current view mode */
  viewMode?: ViewMode
  /** Handler for view mode changes */
  onViewModeChange?: (mode: ViewMode) => void
  /** Available view modes for ViewSwitcher */
  availableViewModes?: ViewMode[]
  /** Filter entries to a specific project */
  projectId?: string
  /** Default entry type filter (overrides the feed's internal default of 'all') */
  defaultFilter?: FilterType
  /** Filter journal entries by prompt category */
  promptCategoryFilter?: PromptCategory
}

/**
 * Lazy-loading wrapper for UnifiedFeed that uses React Query
 * On mobile, data is only fetched when enabled (user clicks "See recent")
 * On desktop, always fetches immediately
 */
export function UnifiedFeedLazy({
  enabled = true,
  searchQuery,
  rawSearchQuery,
  selectedTopics,
  onSearchChange,
  onTopicsChange,
  viewMode,
  onViewModeChange,
  availableViewModes,
  projectId,
  defaultFilter,
  promptCategoryFilter,
}: UnifiedFeedLazyProps) {
  // Show completed tasks state with localStorage persistence
  const [showCompleted, setShowCompleted] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('contour-show-completed') === 'true'
  })

  useEffect(() => {
    localStorage.setItem('contour-show-completed', String(showCompleted))
  }, [showCompleted])

  // On desktop (sm+), always enable; on mobile, respect the enabled prop
  // We detect this by checking if we're rendering (SSR-safe check happens in the query)
  const isDesktop =
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 640px)').matches

  const shouldFetch = isDesktop || enabled
  const { data, isLoading } = useFeedSources(shouldFetch)

  if (isLoading || !data) {
    return <FeedSkeleton />
  }

  return (
    <UnifiedFeed
      journals={data.journals}
      tasks={data.tasks}
      movies={data.movies}
      books={data.books}
      artists={data.artists}
      labels={data.labels}
      releases={data.releases}
      searchQuery={searchQuery}
      rawSearchQuery={rawSearchQuery}
      selectedTopics={selectedTopics}
      onSearchChange={onSearchChange}
      onTopicsChange={onTopicsChange}
      showCompleted={showCompleted}
      onShowCompletedChange={setShowCompleted}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      availableViewModes={availableViewModes}
      projectId={projectId}
      defaultFilter={defaultFilter}
      promptCategoryFilter={promptCategoryFilter}
    />
  )
}

function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {/* Section header skeleton */}
      <Skeleton className="h-5 w-24" />

      {/* Entry skeletons - representative of actual content structure */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-lg border p-4">
          {/* Header row: avatar + type indicator */}
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" className="h-8 w-8" />
            <Skeleton className="h-4 w-24" />
          </div>
          {/* Title */}
          <Skeleton className="h-5 w-3/4" />
          {/* Content preview */}
          <SkeletonText lines={2} />
          {/* Metadata tags */}
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
